import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import { supabase } from "../../../lib/supabase";

const MOCK_FOUNDERS = [
{
  id: "mock-1",
  business_name: "Luminary Health Tech",
  founder_name: "Dr. Amara Johnson",
  founder_title: "CEO & Co-Founder",
  sector: "HealthTech",
  funding_stage: "Series A",
  min_investment: 2500,
  projected_roi: 28,
  description: "AI-powered women\'s health diagnostics platform detecting conditions 3x earlier than traditional methods through machine learning.",
  profile_photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1862ccc14-1772869198004.png",
  is_women_led: true
},
{
  id: "mock-2",
  business_name: "GreenRoot Capital",
  founder_name: "Priya Sharma",
  founder_title: "Founder & Managing Partner",
  sector: "Real Estate",
  funding_stage: "Seed",
  min_investment: 5000,
  projected_roi: 18,
  description: "Sustainable real estate investment platform connecting women investors to eco-friendly commercial properties across the Southeast.",
  profile_photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b79780e9-1772869205961.png",
  is_women_led: true
},
{
  id: "mock-3",
  business_name: "Elevate EdTech",
  founder_name: "Keisha Williams",
  founder_title: "CEO & Chief Learning Officer",
  sector: "Education",
  funding_stage: "Pre-Seed",
  min_investment: 1000,
  projected_roi: 22,
  description: "Personalized STEM learning platform for underserved girls ages 8–18, with 94% course completion rates and Fortune 500 partnerships.",
  profile_photo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19517bc84-1772277546015.png",
  is_women_led: true
}];


function SkeletonCard() {
  return (
    <div
      className="rounded-xl p-4 flex-shrink-0 w-full"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)",
        borderTop: "3px solid var(--color-border)"
      }}>
      
      <div className="flex items-start justify-between mb-3">
        <div className="w-16 h-16 rounded-full shimmer" style={{ background: "var(--color-muted)" }} />
        <div className="w-20 h-6 rounded-full shimmer" style={{ background: "var(--color-muted)" }} />
      </div>
      <div className="w-3/4 h-5 rounded shimmer mb-1" style={{ background: "var(--color-muted)" }} />
      <div className="w-1/2 h-4 rounded shimmer mb-3" style={{ background: "var(--color-muted)" }} />
      <div className="flex gap-2 mb-3">
        <div className="w-16 h-6 rounded-full shimmer" style={{ background: "var(--color-muted)" }} />
        <div className="w-20 h-6 rounded-full shimmer" style={{ background: "var(--color-muted)" }} />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="h-14 rounded-lg shimmer" style={{ background: "var(--color-muted)" }} />
        <div className="h-14 rounded-lg shimmer" style={{ background: "var(--color-muted)" }} />
      </div>
      <div className="w-full h-4 rounded shimmer mb-1" style={{ background: "var(--color-muted)" }} />
      <div className="w-5/6 h-4 rounded shimmer mb-4" style={{ background: "var(--color-muted)" }} />
      <div className="w-full h-9 rounded-lg shimmer mb-2" style={{ background: "var(--color-muted)" }} />
      <div className="w-full h-9 rounded-lg shimmer" style={{ background: "var(--color-muted)" }} />
    </div>);

}

function FounderCard({ founder, onViewOpportunity }) {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);

  const formatROI = (roi) => {
    if (!roi) return "N/A";
    return `${Number(roi)?.toFixed(0)}% ROI`;
  };

  const formatMinInvestment = (amount) => {
    if (!amount) return "Contact for details";
    return `From $${Number(amount)?.toLocaleString()}`;
  };

  return (
    <div
      className="rounded-xl p-4 flex-shrink-0 w-full flex flex-col cursor-pointer"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)",
        borderTop: "3px solid var(--color-accent)",
        transition: "transform 300ms ease-in-out, box-shadow 300ms ease-in-out",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ?
        "0 12px 32px rgba(133,113,77,0.28)" :
        "0 2px 8px rgba(0,0,0,0.06)"
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      
      {/* Top Row: Avatar + Badges */}
      <div className="flex items-start justify-between mb-3">
        {/* Founder Avatar */}
        <div
          className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
          style={{
            border: "2px solid var(--color-accent)",
            boxShadow: "0 0 0 2px rgba(133,113,77,0.2)"
          }}>
          
          {!imgError && founder?.profile_photo_url ?
          <Image
            src={founder?.profile_photo_url}
            alt={`${founder?.founder_name} founder photo`}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)} /> :


          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: "var(--color-primary)" }}>
            
              <Icon name="User" size={28} color="var(--color-accent)" strokeWidth={1.5} />
            </div>
          }
        </div>

        {/* Right Badges */}
        <div className="flex flex-col items-end gap-1">
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-full"
            style={{ background: "rgba(133,113,77,0.12)", border: "1px solid rgba(133,113,77,0.3)" }}>
            
            <Icon name="Crown" size={11} color="var(--color-accent)" strokeWidth={2.5} />
            <span className="font-caption text-xs font-bold" style={{ color: "var(--color-accent)" }}>
              WIN Verified
            </span>
          </div>
          {founder?.is_women_led &&
          <div
            className="px-2 py-0.5 rounded-full"
            style={{ background: "rgba(133,113,77,0.1)", border: "1px solid rgba(133,113,77,0.25)" }}>
            
              <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-accent)" }}>
                Women-Led
              </span>
            </div>
          }
        </div>
      </div>
      {/* Company & Founder Info */}
      <h3
        className="font-heading font-bold text-lg leading-tight mb-0.5"
        style={{ color: "var(--color-primary)", fontFamily: "'Cormorant Garamond', serif" }}>
        
        {founder?.business_name}
      </h3>
      <p className="font-caption text-xs mb-3" style={{ color: "var(--color-accent)" }}>
        {founder?.founder_name}
        {founder?.founder_title ? ` · ${founder?.founder_title}` : ""}
      </p>
      {/* Sector + Stage Tags */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {founder?.sector &&
        <span
          className="font-caption text-xs px-2.5 py-0.5 rounded-full font-medium"
          style={{ background: "var(--color-primary)", color: "white" }}>
          
            {founder?.sector}
          </span>
        }
        {founder?.funding_stage &&
        <span
          className="font-caption text-xs px-2.5 py-0.5 rounded-full font-medium"
          style={{
            background: "transparent",
            border: "1px solid var(--color-accent)",
            color: "var(--color-accent)"
          }}>
          
            {founder?.funding_stage}
          </span>
        }
      </div>
      {/* Stats Row */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div
          className="rounded-lg p-2 text-center"
          style={{ background: "var(--color-muted)" }}>
          
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Icon name="DollarSign" size={11} color="var(--color-muted-foreground)" strokeWidth={2} />
            <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Min. Invest</p>
          </div>
          <p className="font-data font-bold text-xs" style={{ color: "var(--color-primary)" }}>
            {formatMinInvestment(founder?.min_investment)}
          </p>
        </div>
        <div
          className="rounded-lg p-2 text-center"
          style={{ background: "var(--color-muted)" }}>
          
          <div className="flex items-center justify-center gap-1 mb-0.5">
            <Icon name="TrendingUp" size={11} color="var(--color-accent)" strokeWidth={2} />
            <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Projected</p>
          </div>
          <p className="font-data font-bold text-sm" style={{ color: "var(--color-accent)" }}>
            {formatROI(founder?.projected_roi)}
          </p>
        </div>
      </div>
      {/* Description */}
      <p
        className="font-caption text-xs leading-relaxed mb-4 flex-1"
        style={{
          color: "var(--color-muted-foreground)",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden"
        }}>
        
        {founder?.description || "Innovative business seeking strategic investment partners."}
      </p>
      {/* CTA Buttons */}
      <div className="flex flex-col gap-2 mt-auto">
        <button
          onClick={() => onViewOpportunity(founder)}
          className="w-full py-2 px-4 rounded-lg font-caption text-sm font-semibold flex items-center justify-center gap-1.5"
          style={{
            background: "var(--color-accent)",
            color: "var(--color-primary)",
            transition: "transform 200ms ease, filter 200ms ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.02)";
            e.currentTarget.style.filter = "brightness(1.08)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.filter = "brightness(1)";
          }}>
          
          View Opportunity
          <Icon name="ArrowRight" size={14} color="var(--color-primary)" strokeWidth={2.5} />
        </button>
        <button
          className="w-full py-2 px-4 rounded-lg font-caption text-sm font-semibold"
          style={{
            background: "transparent",
            border: "1.5px solid var(--color-primary)",
            color: "var(--color-primary)",
            transition: "background 200ms ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(11,31,59,0.06)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}>
          
          Express Interest
        </button>
      </div>
    </div>);

}

export default function FounderSpotlightCarousel() {
  const navigate = useNavigate();
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollRef = useRef(null);
  const isMock = useRef(false);

  // Determine visible cards count based on container width
  const [visibleCount, setVisibleCount] = useState(3);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width < 480) setVisibleCount(1);else
        if (width < 800) setVisibleCount(2);else
        setVisibleCount(3);
      }
    });
    if (containerRef?.current) observer?.observe(containerRef?.current);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    async function fetchFounders() {
      try {
        const { data, error } = await supabase?.from("showcase_applications")?.select(
          "id, founder_name, business_name, sector, funding_stage, min_investment, projected_roi, description, profile_photo_url, is_women_led"
        )?.eq("status", "approved")?.order("reviewed_at", { ascending: false })?.limit(10);

        if (error) throw error;

        if (data && data?.length > 0) {
          setFounders(data);
          isMock.current = false;
        } else {
          setFounders(MOCK_FOUNDERS);
          isMock.current = true;
        }
      } catch (err) {
        console.error("Error fetching founders:", err);
        setFounders(MOCK_FOUNDERS);
        isMock.current = true;
      } finally {
        setLoading(false);
      }
    }
    fetchFounders();
  }, []);

  const maxIndex = Math.max(0, founders?.length - visibleCount);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
  }, [maxIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => prev <= 0 ? maxIndex : prev - 1);
  }, [maxIndex]);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    if (isPaused || founders?.length <= visibleCount) return;
    autoScrollRef.current = setInterval(goNext, 5000);
    return () => clearInterval(autoScrollRef?.current);
  }, [isPaused, goNext, founders?.length, visibleCount]);

  const handleViewOpportunity = (founder) => {
    navigate("/investment-opportunities", {
      state: { filterBusiness: founder?.business_name }
    });
  };

  const cardWidthPercent = 100 / visibleCount;

  return (
    <section
      ref={containerRef}
      className="rounded-xl overflow-hidden"
      style={{
        background: "var(--color-card)",
        border: "1px solid var(--color-border)"
      }}
      aria-label="Featured Founder Spotlight">
      
      {/* Section Header */}
      <div
        className="flex items-center justify-between px-4 md:px-5 py-4"
        style={{ borderBottom: "1px solid var(--color-border)" }}>
        
        <div className="flex items-center gap-2">
          <Icon name="Crown" size={18} color="var(--color-accent)" strokeWidth={2} />
          <h2
            className="font-heading text-lg md:text-xl font-semibold"
            style={{
              color: "var(--color-primary)",
              fontFamily: "'Cormorant Garamond', serif"
            }}>
            
            Featured Founder Spotlight
          </h2>
        </div>
        <button
          onClick={() => navigate("/investment-opportunities")}
          className="font-caption text-sm font-semibold flex items-center gap-1"
          style={{ color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer" }}>
          
          View All
          <Icon name="ArrowRight" size={14} color="var(--color-accent)" strokeWidth={2.5} />
        </button>
      </div>
      {/* Carousel Body */}
      <div className="px-4 md:px-5 py-4">
        {loading ? (
        /* Skeleton Loaders */
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${visibleCount}, 1fr)` }}>
            {Array.from({ length: visibleCount })?.map((_, i) =>
          <SkeletonCard key={i} />
          )}
          </div>) :
        founders?.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "rgba(133,113,77,0.1)" }}>
            
              <Icon name="Coins" size={28} color="var(--color-accent)" strokeWidth={1.5} />
            </div>
            <p
            className="font-caption text-sm text-center max-w-xs"
            style={{ color: "var(--color-muted-foreground)" }}>
            
              Founder spotlights coming soon — applications are being reviewed
            </p>
          </div>) : (

        /* Carousel */
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}>
          
            {/* Left Arrow */}
            {founders?.length > visibleCount &&
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-8 h-8 rounded-full flex items-center justify-center shadow-md"
            style={{
              background: "var(--color-accent)",
              border: "none",
              cursor: "pointer"
            }}
            aria-label="Previous founder">
            
                <Icon name="ChevronLeft" size={16} color="var(--color-primary)" strokeWidth={2.5} />
              </button>
          }
            {/* Slides Viewport */}
            <div className="overflow-hidden">
              <div
              className="flex"
              style={{
                transform: `translateX(-${currentIndex * cardWidthPercent}%)`,
                transition: "transform 300ms ease-in-out",
                gap: "16px"
              }}>
              
                {founders?.map((founder) =>
              <div
                key={founder?.id}
                style={{
                  minWidth: `calc(${cardWidthPercent}% - ${16 * (visibleCount - 1) / visibleCount}px)`,
                  flexShrink: 0
                }}>
                
                    <FounderCard
                  founder={founder}
                  onViewOpportunity={handleViewOpportunity} />
                
                  </div>
              )}
              </div>
            </div>
            {/* Right Arrow */}
            {founders?.length > visibleCount &&
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-8 h-8 rounded-full flex items-center justify-center shadow-md"
            style={{
              background: "var(--color-accent)",
              border: "none",
              cursor: "pointer"
            }}
            aria-label="Next founder">
            
                <Icon name="ChevronRight" size={16} color="var(--color-primary)" strokeWidth={2.5} />
              </button>
          }
            {/* Dot Indicators */}
            {founders?.length > visibleCount &&
          <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: maxIndex + 1 })?.map((_, i) =>
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === currentIndex ? "20px" : "8px",
                height: "8px",
                background:
                i === currentIndex ?
                "var(--color-accent)" :
                "var(--color-border)",
                border: "none",
                cursor: "pointer",
                padding: 0
              }}
              aria-label={`Go to slide ${i + 1}`} />

            )}
              </div>
          }
          </div>)
        }
      </div>
      {/* Shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        .shimmer {
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
    </section>);

}